import { apiClient } from './client'

/**
 * `GET/PUT/DELETE /api/v1/me/preferences/{key}` (IB-292 server-sync delta) — the small, generic
 * per-user UI-preference endpoint every `TODO(IB-292/server-sync)` in `frontend/src/theme/*
 * Preference.ts` points at. Backend: `UserPreferenceController`/`UserPreferenceService`
 * (`module.identity`), backed by `V71 user_ui_preference`.
 *
 * Only the two keys the backend allow-lists today (`UserPreferenceService.ALLOWED_KEYS`) — adding
 * a third preference primitive to server-sync is a small, additive change on BOTH sides.
 */
export type PreferenceKey = 'theme' | 'dashboardTheme'

interface PreferenceView {
  key: string
  value: string | null
}

/** Never throws — a network/auth failure degrades to "no server value yet" (the caller's existing
 * `localStorage` read stays the source of truth for that session); this is the FAST FALLBACK the
 * story requires, not a hard dependency on the network being up. */
export async function fetchPreference(key: PreferenceKey, signal?: AbortSignal): Promise<string | null> {
  try {
    const { data } = await apiClient.get<PreferenceView>(`/me/preferences/${key}`, { signal })
    return data.value
  } catch {
    return null
  }
}

/** Fire-and-forget from the caller's perspective — `localStorage` (written by the caller
 * synchronously, before this resolves) is the fast path; this is the cross-device sync, best
 * effort. Never throws — a save failure must never surface as a broken theme toggle. */
export async function savePreference(key: PreferenceKey, value: string): Promise<void> {
  try {
    await apiClient.put<PreferenceView>(`/me/preferences/${key}`, { value })
  } catch {
    // best-effort server sync — localStorage already reflects the choice for this session/device
  }
}

/** Counterpart to {@link savePreference} for "reset to default" (e.g. Dashboard "Platform
 * Default"). Same best-effort posture. */
export async function clearPreference(key: PreferenceKey): Promise<void> {
  try {
    await apiClient.del(`/me/preferences/${key}`)
  } catch {
    // best-effort server sync — localStorage already reflects the choice for this session/device
  }
}
